import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import StatusBadge from "@/components/common/StatusBadge";
import CrudDrawer from "@/components/common/CrudDrawer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LEADS } from "@/constants/testIds";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STAGES = [
  { key: "new", label: "New", color: "#3B82F6" },
  { key: "contacted", label: "Contacted", color: "#F59E0B" },
  { key: "qualified", label: "Qualified", color: "#06B6D4" },
  { key: "converted", label: "Converted", color: "#10B981" },
];

const fields = [
  { key: "name", label: "Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "phone", label: "Phone", type: "text" },
  { key: "source", label: "Source", type: "text" },
  { key: "message", label: "Message", type: "textarea" },
  { key: "status", label: "Status", type: "select",
    options: STAGES.map(s => ({ label: s.label, value: s.key })) },
  { key: "tags", label: "Tags", type: "tags" },
];

export default function Leads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const { data } = await api.get("/leads", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(fetchData, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const moveTo = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      setItems((prev) => prev.map((i) => i.id === lead.id ? { ...i, status } : i));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async (values) => {
    try {
      if (editing) await api.patch(`/leads/${editing.id}`, values);
      else await api.post("/leads", values);
      setDrawer(false);
      fetchData();
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmRemove = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/leads/${pendingDelete.id}`);
      setPendingDelete(null);
      fetchData();
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byStage = STAGES.reduce((acc, s) => {
    acc[s.key] = items.filter(i => (i.status || "new") === s.key);
    return acc;
  }, {});

  return (
    <div data-testid={LEADS.root}>
      <PageHeader
        title="Leads"
        subtitle="Track and convert inquiries from your website."
        actions={
          <>
            <div data-testid={LEADS.viewToggle} className="flex items-center rounded-lg glass p-0.5">
              <button onClick={() => setView("kanban")}
                className={`h-8 px-3 text-xs rounded-md flex items-center gap-1.5 ${view === "kanban" ? "bg-white/10 text-white" : "text-white/50"}`}>
                <Icons.Columns3 className="w-3.5 h-3.5" /> Kanban
              </button>
              <button onClick={() => setView("table")}
                className={`h-8 px-3 text-xs rounded-md flex items-center gap-1.5 ${view === "table" ? "bg-white/10 text-white" : "text-white/50"}`}>
                <Icons.List className="w-3.5 h-3.5" /> Table
              </button>
            </div>
            <Button onClick={() => { setEditing(null); setDrawer(true); }} className="btn-gradient text-white border-0">
              <Icons.Plus className="w-4 h-4 mr-1.5" /> New lead
            </Button>
          </>
        }
      />

      <div className="mb-4 max-w-md relative">
        <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…"
          className="pl-9 bg-black/20 border-white/10 text-white placeholder:text-white/30" />
      </div>

      {view === "kanban" ? (
        <div data-testid={LEADS.kanban} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAGES.map((s) => (
            <div key={s.key} className="glass rounded-xl overflow-hidden flex flex-col min-h-[400px]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }} />
                  <span className="text-sm font-medium text-white">{s.label}</span>
                  <span className="text-xs text-white/40 ml-1">{byStage[s.key]?.length || 0}</span>
                </div>
              </div>
              <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                {(byStage[s.key] || []).map((l) => (
                  <div key={l.id} className="glass rounded-lg p-3 hover:bg-white/[0.05] transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-white truncate">{l.name}</div>
                        <div className="text-xs text-white/50 truncate">{l.email}</div>
                      </div>
                      <button onClick={() => { setEditing(l); setDrawer(true); }} className="p-1 rounded hover:bg-white/5 text-white/50 hover:text-white">
                        <Icons.Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {l.message && (
                      <p className="mt-2 text-xs text-white/60 line-clamp-2">{l.message}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] text-white/40 capitalize">{(l.source || "").replace("_", " ")}</span>
                      <div className="flex items-center gap-0.5">
                        {STAGES.filter(x => x.key !== s.key).map(x => (
                          <button key={x.key} onClick={() => moveTo(l, x.key)}
                            className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                            title={`Move to ${x.label}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full block" style={{ background: x.color }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                {!loading && (byStage[s.key] || []).length === 0 && (
                  <div className="text-center text-xs text-white/30 py-6">No leads</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <GlassCard className="p-0" data-testid={LEADS.table}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-white/45 bg-white/[0.02]">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Received</th>
                  <th className="px-5 py-3 font-medium text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-white">{l.name}</td>
                    <td className="px-5 py-3 text-white/60">{l.email}</td>
                    <td className="px-5 py-3 text-white/60 capitalize">{(l.source || "").replace("_", " ")}</td>
                    <td className="px-5 py-3"><StatusBadge value={l.status} /></td>
                    <td className="px-5 py-3 text-white/50 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => { setEditing(l); setDrawer(true); }} className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white">
                        <Icons.Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setPendingDelete(l)} className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-red-300 ml-1">
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      <CrudDrawer
        open={drawer}
        onOpenChange={setDrawer}
        title={editing ? "Edit lead" : "New lead"}
        fields={fields}
        initial={editing || { status: "new" }}
        onSave={save}
        resourceKey="leads"
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="glass-strong border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              “{pendingDelete?.name}” will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
